import React from 'react';
// Optional: Import icons if needed for placeholders or sections
import { UsersIcon, MicrophoneIcon, ChartBarIcon } from '@heroicons/react/24/outline';

const AboutUsPage = () => {
    return (
        // Using theme background
        <div className="bg-base-200 min-h-screen">

            {/* Hero Section - Simple Title */}
            <div className="bg-base-100 shadow-sm py-10 md:py-12"> {/* Adjusted padding slightly */}
                {/* Container centers content */}
                <div className="container mx-auto px-4">
                    {/* Logo Image - Copied from MainMenuPage */}
                    <img
                        src="/twifwave.png" // Make sure this path is correct in /public
                        alt="The Week In Fantasy Logo"
                        // Responsive height, centered, margin bottom
                        className="block mx-auto h-20 md:h-28 lg:h-32 w-auto mb-4" // Adjusted height classes
                    />
                    {/* Tagline - Centered below image */}
                    <p className="text-lg text-center text-base-content/80">
                      
                    </p>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="container mx-auto px-4 py-12 md:py-16 space-y-16">

                {/* Section 1: Our Mission */}
                <section className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                    {/* Text Content */}
                    <div className="md:w-1/2 text-base-content">
                    <h2 className="text-3xl font-semibold mb-4 text-accent">
                        {/* This part keeps the h2's base styles (including text-accent) */}
                        Our Mission:{' '} {/* Added a space for separation */}

                        {/* This span overrides color and font-weight for the rest of the text */}
                        <span className="italic text-white"> {/* Or use text-base-content if white isn't suitable for your theme */}
                            Enabling fantasy managers to build better rosters and win championships by creating efficient access to the vast network of fantasy analysts
                        </span>
                    </h2> 
                        <p className="mb-4">
                            In today's fantasy football landscape, managers are flooded with information. The Week In Fantasy (TWIF) was created to cut through the noise, helping you efficiently leverage the incredible insights from the best content creators and analysts in the game.
                        </p>
                        <p>
                            We believe every manager, no matter how busy, deserves access to expert analysis. Fantasy analysts dedicate their time to scrutinizing data and game play – experience you might not have time to gain yourself. TWIF aggregates diverse perspectives, supporting your crucial lineup decisions: who to start, sit, pick up off waivers, trade for, or trade away. Our goal is to help you build your own trusted "team" of fantasy experts whose opinions supplement your own research.
                        </p>
                    </div>
                    {/* Image Placeholder */}
                    <div className="md:w-1/2">
                        <img
                            src="/decision2.jpg" // Path from the public folder
                            alt="Fantasy football manager making a tough decision" // Descriptive alt text
                            // Apply styling consistent with placeholder/design
                            // w-full: Makes image take the width of its container (md:w-1/2)
                            // aspect-video: Sets a 16:9 aspect ratio
                            // object-cover: Ensures image covers the area without distortion, cropping if needed
                            // rounded-lg & shadow-lg: Match previous placeholder style
                            className="w-full aspect-video object-cover rounded-lg shadow-lg"
                        />
                    </div>
                </section>

                {/* Section 2: More Than Stats */}
                {/* Use md:flex-row-reverse to alternate layout */}
                <section className="flex flex-col md:flex-row-reverse items-center gap-8 md:gap-12">
                    {/* Text Content */}
                    <div className="md:w-1/2 text-base-content">
                        <h2 className="text-3xl font-semibold mb-4 text-accent">More Than Stats: Community & Humor</h2>
                        <p className="mb-4">
                             TWIF also stems from a genuine love for the entire fantasy football echoverse. It's more than just stats; fantasy is a fantastic way to connect and share experiences with friends, family, and colleagues, whether they're next door or across the globe.
                        </p>
                        <p>
                             We aim to capture that spirit by highlighting the week's hot topics, key debates, and the inherent fun and humor of it all. Look out for our upcoming podcast/YouTube show – <strong className="font-italic">"The Week in Fantasy: The Analysis of the Analysis, The Show About the Shows"</strong> – where we'll dive even deeper!
                        </p>
                    </div>
                    {/* Image Placeholder */}
                    <div className="md:w-1/2">
                    <img
                            src="/podcast.jpg" // Path from the public folder
                            alt="Discussing the topics, trend and humor of the week" // Descriptive alt text
                            // Apply styling consistent with placeholder/design
                            // w-full: Makes image take the width of its container (md:w-1/2)
                            // aspect-video: Sets a 16:9 aspect ratio
                            // object-cover: Ensures image covers the area without distortion, cropping if needed
                            // rounded-lg & shadow-lg: Match previous placeholder style
                            className="w-full aspect-video object-cover rounded-lg shadow-lg"
                        />
                    </div>
                </section>

                {/* Section 3: Supporting the Community */}
                <section className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                    {/* Text Content */}
                    <div className="md:w-1/2 text-base-content">
                        <h2 className="text-3xl font-semibold mb-4 text-accent">Supporting the Ecosystem</h2>
                        <p className="mb-4">
                            Finally, and most importantly, we have immense appreciation for the talented content creators and dedicated analysts who make the fantasy community thrive.
                        </p>
                         <p>
                            A core goal of TWIF is to amplify their voices and provide valuable exposure, helping them connect with a wider audience and grow their platforms. We believe in supporting the ecosystem – we succeed when the creators succeed.
                         </p>
                    </div>
                    {/* Image Placeholder */}
                    <div className="md:w-1/2">
                    <img
                            src="/growth2.png" // Path from the public folder
                            alt="Drive growth for content creators and analysts" // Descriptive alt text
                            // Apply styling consistent with placeholder/design
                            // w-full: Makes image take the width of its container (md:w-1/2)
                            // aspect-video: Sets a 16:9 aspect ratio
                            // object-cover: Ensures image covers the area without distortion, cropping if needed
                            // rounded-lg & shadow-lg: Match previous placeholder style
                            className="w-full aspect-video object-cover rounded-lg shadow-lg"
                        />
                    </div>
                </section>

                {/* Optional: Call to Action or Founder Note */}
                <section className="text-center border-t border-base-300 pt-12 mt-12"> {/* Added mt-12 for spacing */}
                    <h3 className="text-2xl font-semibold mb-4 text-accent">
                        Get Involved with TWIF
                    </h3>
                    <p className="mb-6 max-w-2xl mx-auto text-base-content/80">
                        Are you interested in investing in the future of fantasy football analysis,
                        or potentially becoming part of the TWIF team? We'd love to hear from you.
                    </p>
                    {/* Button that triggers email client via JS */}
                    <button
                        className="btn btn-accent shadow-md" // Changed color to accent, added shadow
                        data-user="info" // Store parts of the email separately
                        data-domain="theweekinfantasy.com"
                        onClick={(e) => {
                            // Simple JS to construct mailto link on click
                            const user = e.currentTarget.getAttribute('data-user');
                            const domain = e.currentTarget.getAttribute('data-domain');
                            if (user && domain) {
                                // Optional: Add a default subject line
                                const subject = encodeURIComponent("Inquiry about TWIF (Investment/Team)");
                                window.location.href = `mailto:${user}@${domain}?subject=${subject}`;
                            } else {
                                console.error("Could not construct email address.");
                                // Optionally show an error to the user
                                alert("Sorry, there was an error creating the email link.");
                            }
                        }}
                    >
                        Contact Us
                    </button>
                </section>

            </div>
        </div>
    );
};

export default AboutUsPage;